import { jwtDecode } from "jwt-decode";
import { useContext, useEffect, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { GenAiContext } from "../../context";
import {
  accessTokenState,
  authState,
  threadHistoryState,
  userState,
} from "../../state";
import { IUser } from "../../types";
import { getToken, removeToken, setToken } from "../../utils/token";
import { DEFAULT_AUTH_CONFIG } from "@/utils/config";

export const useAuth = () => {
  const apiClient = useContext(GenAiContext);
  const [authConfig, setAuthConfig] = useRecoilState(authState);
  const [user, setUser] = useRecoilState(userState);
  const [accessToken, setAccessToken] = useRecoilState(accessTokenState);
  const setThreadHistory = useSetRecoilState(threadHistoryState);

  useEffect(() => {
    setAuthConfig(DEFAULT_AUTH_CONFIG);
  }, []);

  const [isReady, setReady] = useState(false);

  const logout = async (reload = false) => {
    try {
      await apiClient.logout(accessToken || "");
    } catch (error) {
      console.error(error);
    } finally {
      setUser(null);
      removeToken();
      setAccessToken("");
      setThreadHistory(undefined);
      if (reload) {
        window.location.reload();
      }
    }
  };

  const saveAndSetToken = (
    token: string | null | undefined,
    refresh?: string | null | undefined
  ) => {
    if (!token) {
      logout();
      return;
    }
    try {
      const user = jwtDecode(token) as any;
      if (refresh) {
        setToken(refresh, "refresh_token");
      }

      setToken(token);
      setAccessToken(`Bearer ${token}`);
      setUser({
        id: user.user_id,
        display_name: user.full_name,
        ...user,
      } as IUser);
    } catch (e) {
      console.error(
        "Invalid token, clearing token from local storage",
        "error:",
        e
      );
      logout();
    }
  };

  useEffect(() => {
    if (!isReady) {
      // race condition
      const timer = setTimeout(() => {
        setReady(true);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (!user && getToken()) {
      // Initialize the token from local storage
      saveAndSetToken(getToken(), getToken("refresh"));
      return;
    }
  }, []);

  const isAuthenticated = !!accessToken;

  if (authConfig && !authConfig.requireLogin) {
    return {
      authConfig,
      user: null,
      isReady,
      isAuthenticated: true,
      accessToken: "",
      logout: () => {},
      setAccessToken: () => {},
    };
  }

  return {
    data: authConfig,
    user: user,
    setUser: setUser,
    isAuthenticated,
    isReady,
    accessToken: accessToken,
    logout: logout,
    setAccessToken: saveAndSetToken,
  };
};
